/**
 * AuthToast — a small, centered, auto-dismissing confirmation shown after a
 * successful native (modal-driven) sign-in, so the user gets clear feedback that
 * the silent modal-close actually logged them in. Driven by AuthContext's
 * `authToast` signal; inherits partner branding via --partner-*.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const VISIBLE_MS = 2600;

export const AuthToast: React.FC = () => {
  const { t } = useTranslation('auth');
  const { authToast } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState<'success' | 'danger'>('success');

  // New toast id → (re)show, then fade out.
  useEffect(() => {
    if (!authToast) return;
    setMessage(t(authToast.key, authToast.params));
    setVariant(authToast.variant);
    setMounted(true);
    const raf = requestAnimationFrame(() => setVisible(true));
    const hide = setTimeout(() => setVisible(false), VISIBLE_MS);
    return () => { cancelAnimationFrame(raf); clearTimeout(hide); };
  }, [authToast, t]);

  // Unmount once the fade-out finishes.
  useEffect(() => {
    if (visible) return;
    const timer = setTimeout(() => setMounted(false), 250);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!mounted || !message) return null;

  const danger = variant === 'danger';
  const Icon = danger ? LogOut : CheckCircle2;

  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed', top: 24, left: 0, right: 0, zIndex: 1090,
        display: 'flex', justifyContent: 'center', pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          display: 'inline-flex', alignItems: 'center', gap: 11,
          padding: '14px 24px', borderRadius: 12,
          background: danger ? '#dc3545' : 'var(--partner-modal-header-bg, #14233a)',
          color: danger ? '#ffffff' : 'var(--partner-modal-header-fg, #ffffff)',
          boxShadow: '0 12px 32px rgba(0,0,0,.32)',
          fontWeight: 600, fontSize: 19, whiteSpace: 'nowrap', maxWidth: '90vw', overflow: 'hidden', textOverflow: 'ellipsis',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      >
        <Icon size={24} style={{ color: danger ? '#ffffff' : '#4ade80', flexShrink: 0 }} /> {message}
      </div>
    </div>
  );
};
